import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { shareUrl, toShareLink } from "@/lib/share";
import { publicOrigin } from "@/lib/publicOrigin";

// Thin wrapper kept for call sites that used the {dialogTitle, copiedMessage} shape.
// Delegates to shareUrl() which converts the URL to its Open Graph link (rich card)
// and opens the native OS share sheet on the app / mobile web, else copies the link.
export async function nativeShare({ title = "Yalola", text = "", url = "", dialogTitle = "Partager", copiedMessage = "Lien copié 🔗" } = {}) {
  const ok = await shareUrl({ title, text, url, dialogTitle, copiedMsg: copiedMessage });
  return ok ? "shared" : "cancelled";
}

const isCancel = (e) => {
  const name = e && (e.name || "");
  return name === "AbortError" || /cancel|abort/i.test(String(e || ""));
};

const blobToDataUrl = (blob) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(blob);
});

// ONE message everywhere: text + link only. WhatsApp / Telegram / Messenger render the
// Yalola card (photo, name, description) from the /api/e/… Open Graph tags INSIDE the same
// bubble, with the tappable link that opens the app. Attaching the poster as a file made
// WhatsApp (iOS especially) split the share into two bubbles (text, then image) — the
// poster itself stays available through the « Télécharger » buttons.
// Native app → OS share sheet ; mobile web → Web Share API ; otherwise → copy text + link.
// Returns true when shared/copied, false when the member cancelled.
export async function shareImageOrLink({ title = "Yalola", text = "", url = "", copiedMessage = "Lien copié 🔗" } = {}) {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title, text, url: url || undefined });
      return true;
    } catch (e) {
      if (isCancel(e)) return false;
      // fall through to web / copy
    }
  }

  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share({ title, text, url: url || undefined });
      return true;
    }
  } catch (e) {
    if (isCancel(e)) return false;
  }

  try {
    await navigator.clipboard.writeText([text, url].filter(Boolean).join("\n"));
    toast.success(copiedMessage);
    return true;
  } catch { return false; }
}

// Share a post/reel: text + its Open Graph link (rich Yalola card in the same bubble).
export async function sharePostCard({ postId, title = "Yalola", text = "", copiedMessage = "Lien copié 🔗" } = {}) {
  return shareImageOrLink({ title, text, url: `${publicOrigin()}/api/e/post/${postId}`, copiedMessage });
}

// Share any internal link as its Open Graph link (rich Yalola card in the same bubble).
export async function shareLinkCard({ url, title = "Yalola", text = "", copiedMessage = "Lien copié 🔗" } = {}) {
  return shareImageOrLink({ title, text, url: toShareLink(url), copiedMessage });
}


// "Download" a generated file (GDPR export, receipts…) the way each platform can:
// - Native app → save it to the app's Documents (visible in Files › Yalola on iOS,
//   never lost even if the share sheet fails), then open the OS share sheet so the
//   member picks where it goes (Fichiers / Drive / WhatsApp…). A WebView has no
//   download manager, so the classic invisible <a download> silently does nothing.
// - Web → regular browser download.
// Returns "shared" | "saved" (sheet unavailable, file kept in Documents) | "downloaded" | "cancelled".
export async function saveOrShareFile({ blob, fileName = "yalola.json", title = "Yalola" } = {}) {
  if (Capacitor.isNativePlatform()) {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const dataUrl = await blobToDataUrl(blob);
    const base64 = String(dataUrl).split(",")[1];
    const path = `${fileName.replace(/\.[a-z0-9]+$/i, "")}-${Date.now()}${(fileName.match(/\.[a-z0-9]+$/i) || [""])[0]}`;
    // iOS: Documents = « Fichiers › Yalola » (UIFileSharingEnabled). Android: Documents is the
    // PUBLIC folder (storage permissions on 10+) → keep the app cache, shared via FileProvider.
    const directory = Capacitor.getPlatform() === "ios" ? Directory.Documents : Directory.Cache;
    await Filesystem.writeFile({ path, data: base64, directory });
    const { uri } = await Filesystem.getUri({ path, directory });
    try {
      await Share.share({ title, files: [uri] });
      return "shared";
    } catch (e) {
      if (isCancel(e)) return "cancelled";
      console.warn("saveOrShareFile: share sheet failed, file kept in Documents", e);
      return "saved";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fileName; a.click();
  URL.revokeObjectURL(url);
  return "downloaded";
}
